import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useState, useEffect } from 'react';

import { Iconify } from 'src/components/iconify';
import {
  get_personal_member,
  unassign_members_to_program,
  send_request_to_member_plates,
  send_request_to_members,
} from 'src/auth/context/jwt/program';
import { toast } from 'sonner';
import { LoadingButton } from '@mui/lab';

// ----------------------------------------------------------------------

export function MembersManagementDialog({ open, onClose, activity, onRefresh }) {
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [allMembers, setAllMembers] = useState([]);
  const [assignedMembers, setAssignedMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedMembers([]);
      setSearchQuery('');
      setError(null);
      if (activity) {
        setAssignedMembers(activity?.members || []);
        fetchAllMembers();
      }
    }
  }, [open, activity]);

  // Mock API call to fetch all members - replace with your actual API
  const fetchAllMembers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Simulate API delay

      const res = await get_personal_member();
      if (res?.status === 200 || res?.status === 201) {
        setAllMembers(res?.data?.data);
      }
    } catch (err) {
      setError('Failed to load members. Please try again.');
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unassigned members with search filter
  const getFilteredUnassignedMembers = () => {
    const assignedIds = assignedMembers.map((m) => m.id);
    const unassigned = allMembers.filter((member) => !assignedIds.includes(member.id));

    if (!debouncedSearch) return unassigned;

    return unassigned.filter(
      (member) =>
        member.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        member.email?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  };

  // Get filtered assigned members for display in modal
  const getFilteredAssignedMembers = () => {
    if (!debouncedSearch) return assignedMembers;

    return assignedMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        member.email?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  };

  // Handle member selection for assignment
  const handleMemberToggle = (member) => {
    const isSelected = selectedMembers.some((m) => m.id === member.id);
    if (isSelected) {
      setSelectedMembers(selectedMembers.filter((m) => m.id !== member.id));
    } else {
      setSelectedMembers([...selectedMembers, member]);
    }
  };

  const [assigning, setassigning] = useState(false);

  const handleAssignMembers = async () => {
    setassigning(true);
    try {
      const res = await send_request_to_members({
        original_id: activity?.original_id,
        program_id: activity?.id,
        member_ids: selectedMembers.map((m) => m.id),
      });
      toast.success(res?.message);
      // Refresh assigned members list
      setAssignedMembers((prev) => [...prev, ...selectedMembers]);
      // Clear selected members
      setSelectedMembers([]);
      // Optionally, refresh all members list from server
      fetchAllMembers();
      // Close dialog
      onClose();

      onRefresh();
    } catch (e) {
      console.log(e);
    } finally {
      setassigning(false);
    }
  };

  const [unassigning, setunassigning] = useState(false);
  const [currentMember, setcurrentMember] = useState(null);
  // Handle unassign member
  const handleUnassignMember = async (member) => {
    setunassigning(true);
    setcurrentMember(member?.id);
    try {
      const res = await unassign_members_to_program({
        original_id: activity?.original_id,
        program_id: activity?.id,
        member_id: member?.id,
      });
      toast.success(res?.message);
      // Refresh assigned members list
      setAssignedMembers((prev) => prev.filter((m) => m.id !== member.id));
      // Optionally, refresh all members list from server
      fetchAllMembers();
    } catch (e) {
      console.log(e);
    } finally {
      setunassigning(false);
      setcurrentMember(null);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Handle dialog close
  const handleClose = () => {
    setSelectedMembers([]);
    setSearchQuery('');
    setError(null);
    onClose();
  };

  // Handle assigning members to activity

  const filteredUnassignedMembers = getFilteredUnassignedMembers();
  const filteredAssignedMembers = getFilteredAssignedMembers();

  if (!activity) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Iconify icon="eva:person-add-fill" />
          <Box>
            <Typography variant="h6">Manage Program Members</Typography>
            <Typography variant="body2" color="text.secondary">
              Program: {activity.title}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 3 }}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <Iconify icon="eva:close-fill" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                Loading members...
              </Typography>
            </Stack>
          </Box>
        ) : (
          <>
            {/* Currently Assigned Members */}
            {assignedMembers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="success.main">
                    Currently Assigned
                  </Typography>
                  <Chip
                    label={assignedMembers.length}
                    size="small"
                    color="success"
                    variant="soft"
                  />
                </Stack>

                {filteredAssignedMembers.length > 0 ? (
                  <List
                    dense
                    sx={{
                      maxHeight: 200,
                      overflow: 'auto',
                      bgcolor: 'background.neutral',
                      borderRadius: 1,
                    }}
                  >
                    {filteredAssignedMembers.map((member, index) => (
                      <ListItem
                        key={member.id}
                        divider={filteredAssignedMembers.length - 1 !== index}
                        // secondaryAction={
                        //   <IconButton
                        //     disabled={unassigning}
                        //     edge="end"
                        //     size="small"
                        //     onClick={() => handleUnassignMember(member)}
                        //     sx={{ color: 'error.main' }}
                        //   >
                        //     {unassigning && currentMember === member?.id ? (
                        //       <CircularProgress size={20} />
                        //     ) : (
                        //       <Iconify icon="eva:close-circle-fill" />
                        //     )}
                        //   </IconButton>
                        // }
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={member.avatar}
                            alt={member.name}
                            sx={{ width: 36, height: 36 }}
                          >
                            {member.name.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={member.name}
                          secondary={member.email}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : searchQuery ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center', py: 2 }}
                  >
                    No assigned members match `{searchQuery}`
                  </Typography>
                ) : null}
              </Box>
            )}

            {/* Available Members to Assign */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary.main">
                Available Members
              </Typography>
              <Chip
                label={filteredUnassignedMembers.length}
                size="small"
                color="primary"
                variant="soft"
              />
              {selectedMembers.length > 0 && (
                <Chip
                  label={`${selectedMembers.length} selected`}
                  size="small"
                  color="info"
                  variant="soft"
                />
              )}
            </Stack>

            {filteredUnassignedMembers.length > 0 ? (
              <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                {filteredUnassignedMembers.map((member) => (
                  <ListItemButton
                    key={member.id}
                    onClick={() => handleMemberToggle(member)}
                    divider
                    selected={selectedMembers.some((m) => m.id === member.id)}
                  >
                    <ListItemAvatar>
                      <Avatar src={member.avatar} alt={member.name} sx={{ width: 36, height: 36 }}>
                        {member.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.name}
                      secondary={member.email}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Checkbox
                      edge="end"
                      checked={selectedMembers.some((m) => m.id === member.id)}
                      onChange={() => handleMemberToggle(member)}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : searchQuery ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 3 }}
              >
                No available members match {searchQuery}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 3 }}
              >
                All members are already assigned to this activity.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
        <LoadingButton
          loading={assigning}
          variant="contained"
          onClick={handleAssignMembers}
          disabled={selectedMembers.length === 0 || loading}
          startIcon={<Iconify icon="eva:checkmark-fill" />}
        >
          Assign {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
